import type { AppState } from './models';
import type { WorldSpaceId } from './worldSpaces';

export type WeeklyPillarId = 'record' | 'mission' | 'economy' | 'learning' | 'family';
export type WeeklyExpeditionPhase = 'start' | 'momentum' | 'near-summit' | 'complete';

export interface WeeklyPillarDefinition {
  id: WeeklyPillarId;
  title: string;
  shortTitle: string;
  copy: string;
  actionLabel: string;
  route: string;
  spaceId: WorldSpaceId;
  target: number;
  rewardXp: number;
  icon: string;
  count(state: AppState, start: Date, end: Date): number;
}

export interface WeeklyPillarState extends WeeklyPillarDefinition {
  current: number;
  percent: number;
  completed: boolean;
  recommended: boolean;
}

export interface WeeklyExpedition {
  pillars: WeeklyPillarState[];
  nextFocus: WeeklyPillarState;
  completedCount: number;
  totalActions: number;
  completedActions: number;
  percent: number;
  phase: WeeklyExpeditionPhase;
  complete: boolean;
  weekStart: string;
  weekEnd: string;
  weekLabel: string;
  cycleWeek: number;
  cycleTheme: string;
  cycleCopy: string;
  summary: string;
  rewardXp: number;
}

const A = '/assets/ui-icons';
const dayMs = 86_400_000;

function startOfWeek(value: Date): Date {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
}

function endOfWeek(value: Date): Date {
  const date = startOfWeek(value);
  date.setDate(date.getDate() + 7);
  return date;
}

function inRange(value: string | undefined, start: Date, end: Date): boolean {
  if (!value) return false;
  const time = new Date(value).getTime();
  return Number.isFinite(time) && time >= start.getTime() && time < end.getTime();
}

function formatMonthDay(value: Date): string {
  return `${value.getMonth() + 1}월 ${value.getDate()}일`;
}

function cycleInfo(now: Date) {
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const cycleWeek = Math.min(4, Math.max(1, Math.floor((now.getTime() - monthStart.getTime()) / (7 * dayMs)) + 1));
  const themes = [
    ['관찰 주간', '내 돈과 감정을 기록하며 이번 달의 출발점을 발견해요.'],
    ['선택 주간', '필요와 욕구를 비교하고 내가 정한 행동을 직접 실천해요.'],
    ['연결 주간', '경제 개념을 현실과 가족의 대화에 연결해요.'],
    ['회고 주간', '이번 달에 달라진 선택을 돌아보고 다음 작은 정상을 정해요.'],
  ] as const;
  return { cycleWeek, cycleTheme: themes[cycleWeek - 1][0], cycleCopy: themes[cycleWeek - 1][1] };
}

export const WEEKLY_PILLARS: WeeklyPillarDefinition[] = [
  {
    id: 'record', title: '돈의 흐름 세 번 바라보기', shortTitle: '돈 기록',
    copy: '받고, 쓰고, 모은 돈을 세 번 기록하며 이번 주의 흐름을 발견해요.',
    actionLabel: '이번 주 돈 기록하기', route: '/child/records', spaceId: 'basecamp', target: 3, rewardXp: 30,
    icon: `${A}/mrm_ui_01a_record_v01.png`,
    count: (state, start, end) => state.records.filter((item) => inRange(item.occurredAt, start, end)).length,
  },
  {
    id: 'mission', title: '선택 미션 두 번 실천하기', shortTitle: '선택 미션',
    copy: '소비·저축·나눔과 관련된 작은 행동을 두 번 실천해 통제감을 키워요.',
    actionLabel: '이번 주 미션 시작', route: '/child/missions', spaceId: 'market', target: 2, rewardXp: 40,
    icon: `${A}/mrm_ui_12a_mission_v01.png`,
    count: (state, start, end) => state.missions.filter((item) => inRange(item.submittedAt ?? item.approvedAt, start, end)).length,
  },
  {
    id: 'economy', title: '경제의 날씨 한 번 읽기', shortTitle: '경제 퀴즈',
    copy: '기사 한 줄과 퀴즈 한 번으로 숫자 뒤의 이유를 이해해요.',
    actionLabel: '경제기사 퀴즈 풀기', route: '/child/concepts/economy', spaceId: 'news', target: 1, rewardXp: 40,
    icon: `${A}/mrm_ui_12d_concept_v01.png`,
    count: (state, start, end) => state.quizAttempts.filter((item) => inRange(item.completedAt, start, end)).length,
  },
  {
    id: 'learning', title: '경제 페이지 한 장 복원하기', shortTitle: '개념 학습',
    copy: '도서관의 개념 한 장을 끝까지 배우고 내 생활의 선택과 연결해요.',
    actionLabel: '경제도서관 탐험', route: '/child/concepts', spaceId: 'library', target: 1, rewardXp: 40,
    icon: `${A}/mrm_ui_07_missiontip_v01.png`,
    count: (state, start, end) => state.learningProgress.filter((item) => item.completed && inRange(item.completedAt ?? item.startedAt, start, end)).length,
  },
  {
    id: 'family', title: '가족과 선택 한 번 나누기', shortTitle: '가족 대화',
    copy: '결과를 평가하기보다 선택한 이유를 가족과 이야기하고 응원을 기억으로 남겨요.',
    actionLabel: '가족 탐험 이어가기', route: '/child/family-missions', spaceId: 'fridge', target: 1, rewardXp: 50,
    icon: `${A}/mrm_ui_08_familytalk_v01.png`,
    count: (state, start, end) => state.messages.filter((item) => inRange(item.createdAt, start, end)).length
      + state.familyMissionChallenges.filter((item) => inRange(item.submittedAt ?? item.reviewedAt, start, end)).length,
  },
];

export function deriveWeeklyExpedition(state: AppState, now = new Date()): WeeklyExpedition {
  const start = startOfWeek(now);
  const end = endOfWeek(now);
  const raw = WEEKLY_PILLARS.map((pillar) => {
    const current = Math.min(pillar.target, Math.max(0, pillar.count(state, start, end)));
    return { ...pillar, current, percent: Math.round((current / pillar.target) * 100), completed: current >= pillar.target };
  });
  const focusIndex = raw.findIndex((pillar) => !pillar.completed);
  const safeFocusIndex = focusIndex === -1 ? raw.length - 1 : focusIndex;
  const pillars = raw.map((pillar, index) => ({ ...pillar, recommended: index === safeFocusIndex }));
  const totalActions = pillars.reduce((sum, pillar) => sum + pillar.target, 0);
  const completedActions = pillars.reduce((sum, pillar) => sum + pillar.current, 0);
  const percent = Math.round((completedActions / totalActions) * 100);
  const completedCount = pillars.filter((pillar) => pillar.completed).length;
  const complete = completedCount === pillars.length;
  const phase: WeeklyExpeditionPhase = complete ? 'complete' : percent >= 75 ? 'near-summit' : percent >= 30 ? 'momentum' : 'start';
  const nextFocus = pillars[safeFocusIndex];
  const cycle = cycleInfo(now);
  const rewardXp = pillars.reduce((sum, pillar) => sum + pillar.rewardXp, 0);
  return {
    pillars,
    nextFocus,
    completedCount,
    totalActions,
    completedActions,
    percent,
    phase,
    complete,
    weekStart: start.toISOString(),
    weekEnd: new Date(end.getTime() - dayMs).toISOString(),
    weekLabel: `${formatMonthDay(start)}–${formatMonthDay(new Date(end.getTime() - dayMs))}`,
    ...cycle,
    summary: complete
      ? '이번 주 다섯 가지 성장 힘을 모두 완성했어요. 주간 회고에서 다음 작은 정상을 정해요.'
      : `${nextFocus.shortTitle} ${nextFocus.target - nextFocus.current}번을 더하면 이번 주 눈꽃이 더 선명해져요.`,
    rewardXp,
  };
}
